import { ApiError } from '../../api/client';
import { POSTABLE_TYPES, type MovementType, type Posting } from './api';

export interface StockOperationDraft {
  mode: 'post' | 'transfer';
  type: MovementType;
  quantity: string;
  note: string;
  from: string | null;
  to: string | null;
}

export const EMPTY_OPERATION: StockOperationDraft = {
  mode: 'post', type: 'receipt', quantity: '', note: '', from: null, to: null,
};

export function normalizeQuantity(value: string): string | null {
  const normalized = value.trim().replace(',', '.');
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(normalized)) return null;
  const quantity = Number(normalized);
  return Number.isFinite(quantity) && quantity > 0 ? normalized : null;
}

export function operationFailure(error: unknown): { code: string; uncertain: boolean } {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408) {
    const code = error.status === 403 ? 'insufficient_permissions' : error.status === 401 ? 'not_authenticated' : error.code;
    return { code, uncertain: false };
  }
  return { code: error instanceof ApiError ? error.code : 'unknown', uncertain: true };
}

export function requirePosting(result: unknown): Posting {
  const posting = result as Partial<Posting> | null;
  if (
    !posting || !Array.isArray(posting.movements) || !posting.movements.length ||
    !Array.isArray(posting.negative) ||
    !posting.movements.every((movement) => movement && typeof movement.id === 'string' &&
      typeof movement.component_id === 'string' && typeof movement.movement_type === 'string' &&
      typeof movement.quantity === 'string' && Number.isFinite(Number(movement.quantity))) ||
    !posting.negative.every((balance) => balance && typeof balance.component_id === 'string' &&
      typeof balance.qty === 'string' && Number.isFinite(Number(balance.qty)))
  ) {
    throw new Error('invalid_posting_response');
  }
  return posting as Posting;
}

export function readPendingOperation(key: string): StockOperationDraft | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<StockOperationDraft>;
    if (
      !draft || (draft.mode !== 'post' && draft.mode !== 'transfer') ||
      !draft.type || !POSTABLE_TYPES.includes(draft.type) ||
      typeof draft.quantity !== 'string' || !normalizeQuantity(draft.quantity) ||
      typeof draft.note !== 'string' ||
      (draft.from !== null && typeof draft.from !== 'string') ||
      (draft.to !== null && typeof draft.to !== 'string')
    ) return null;
    return draft as StockOperationDraft;
  } catch {
    return null;
  }
}

export function storePendingOperation(key: string, draft: StockOperationDraft | null): void {
  try {
    if (draft) sessionStorage.setItem(key, JSON.stringify(draft));
    else sessionStorage.removeItem(key);
  } catch {
    return;
  }
}
